import { memo, useEffect, useState, VFC } from 'react'
import { usePR } from 'src/api/promotion'
import { deleteGroup, hideRoom, restoreDeletedGroup } from 'src/api/room'
import { SideMenuLoading } from 'src/components/Loading'
import { useErrorMessageModal } from 'src/hooks/useModal'
import { MatchingStatusType, Room, RoomStatusType } from 'src/models/Room'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { SWRInfiniteResponse } from 'swr/infinite'
import { useMessageContext } from '../MessageContext'
import { GroupDeleteModal } from '../Modals/GroupDeleteModal'
import { GroupRestoreModal } from '../Modals/GroupRestoreModal'
import { RoomHidingModal } from '../Modals/RoomHidingModal'
import { PRItem } from './PRItem'
import { RoomsComponent } from './Rooms'
import { Searchbar } from './Searchbar'

export { RoomsList }

export type RoomsListProps = {
  userId: number
  isReferrer: boolean
  isSalesDemo: boolean
  isLoadingInitialData: boolean
  currentRooms: Room[]
  currentRoom: Room | undefined
  markAsUnreadFn: (room: Room) => Promise<void>
  mutateRooms: SWRInfiniteResponse<Room[], any>['mutate']
  FecthMoreTrigger: VFC
  showDeleted: boolean
  roomName: string
  hasPaid: boolean
  activeMatchingStatus: MatchingStatusType
  activeRoomStatus: RoomStatusType
}
const RoomsListComponent: VFC<RoomsListProps & { className?: string }> = memo(
  ({
    userId,
    isReferrer,
    isSalesDemo,
    className,
    isLoadingInitialData,
    currentRooms,
    currentRoom,
    markAsUnreadFn,
    mutateRooms,
    FecthMoreTrigger,
    showDeleted,
    roomName,
    hasPaid,
    activeRoomStatus,
    activeMatchingStatus,
  }) => {
    const { open } = useErrorMessageModal()

    const [hidingRoom, setHidingRoom] = useState<Room>()
    const [loading, setLoading] = useState<boolean>(isLoadingInitialData)
    const { data } = usePR()

    const { room } = useMessageContext()

    useEffect(() => {
      setLoading(isLoadingInitialData)
    }, [isLoadingInitialData])

    return (
      // NOTE: 画面幅が狭い時にルームが選択されている場合は非表示
      <Section className={className} inRoom={!!room}>
        <Header>
          <Headline>メッセージ</Headline>
        </Header>
        <Searchbar
          activeRoomName={roomName}
          setLoading={setLoading}
          activeMatchingStatus={activeMatchingStatus}
          activeRoomStatus={activeRoomStatus}
        />
        {loading && <SideMenuLoading isLoading={true} />}
        {!loading && !isSalesDemo && data && <PRItem data={data} />}
        {!loading && currentRooms.length === 0 && (
          <NoMessageText>メッセージはまだありません</NoMessageText>
        )}
        {!loading && currentRooms.length > 0 && (
          <RoomsComponent
            rooms={currentRooms}
            FecthMoreTrigger={FecthMoreTrigger}
            currentRoomUniqueId={currentRoom?.uniqueId}
            showDeleted={showDeleted}
            markAsUnread={markAsUnreadFn}
            setHidingRoom={setHidingRoom}
            hasPaid={hasPaid}
          />
        )}
        {hidingRoom && (!hidingRoom.isGroup || !isReferrer) && (
          <RoomHidingModal
            hide={async () => {
              const res = await hideRoom(
                hidingRoom.id,
                hidingRoom.type,
                hidingRoom.hiddenUrl,
                hidingRoom.uniqueId,
                userId,
                mutateRooms,
              )
              if (res.error)
                open({ message: res.error?.errorMessages?.join('\n') })
            }}
            close={() => setHidingRoom(undefined)}
          />
        )}
        {hidingRoom?.isGroup &&
          isReferrer &&
          (showDeleted ? (
            <GroupRestoreModal
              restoreDeletedGroup={async () => {
                const res = await restoreDeletedGroup({ roomId: hidingRoom.id })
                if (res.error)
                  open({ message: res.error?.errorMessages?.join('\n') })

                return
              }}
              close={() => setHidingRoom(undefined)}
            />
          ) : (
            <GroupDeleteModal
              deleteGroup={async () => {
                const res = await deleteGroup({ roomId: hidingRoom.id })
                if (res.error)
                  open({ message: res.error?.errorMessages?.join('\n') })

                return
              }}
              close={() => setHidingRoom(undefined)}
            />
          ))}
      </Section>
    )
  },
)
const RoomsList = styled(RoomsListComponent)``

const Header = styled.div`
  margin-bottom: 8px;
`
const Headline = styled.h2`
  font-size: 18px;
  font-weight: ${fontWeightBold};
`
const Section = styled.section<{ inRoom: boolean }>`
  padding: 16px 16px 0;
  @media screen and (max-width: 960px) {
    padding: 0 16px 0;
    ${({ inRoom }) => inRoom && 'display: none;'}
  }
  display: flex;
  flex-direction: column;
  height: 100%;

  ${RoomsComponent} {
    margin-top: 10px;
  }
`

const NoMessageText = styled.p`
  margin-top: 20px;
  font-size: 12px;
  text-align: center;
`
